import { FormEvent, useState } from 'react';
import Button from '@/components/Button';
import SearchForm from '@/components/search/SearchForm';
import useSearch from '@/components/search/useSearch';
import SupplierSelectBox from '@/components/SupplierSelectBox';
import { Supplier } from '@/models/Supplier';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import ProductTable from '@/modules/product/components/ProductTable';
import { ProductAddPath, ProductImportPath } from '@/modules/product/paths';
import SettingsHeader from '@/modules/settings/components/SettingsHeader';
import { CubeIcon, DocumentArrowDownIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useParams, useSearchParams } from 'react-router-dom';

interface State {
  supplierId: string | null;
}

const ProductList = () => {
  const { currentUser } = useAuth();

  const { storeId } = currentUser();

  const { productCategoryId } = useParams();

  const [searchParams, setSearchParams] = useSearchParams();

  const { searchQuery, currentSearchQuery, page, handlePageChange, setSearchQuery } = useSearch();

  const supplierIdParam = searchParams.get('supplierId');

  const [state, setState] = useState<State>({
    supplierId: supplierIdParam,
  });

  const search = (searchQuery: string | null, supplierId: string | null) => {
    setSearchParams(
      new URLSearchParams({
        page: '1',
        query: searchQuery || '',
        supplierId: supplierId || '',
      }),
    );
  };

  const { supplierId } = state;

  const handleSupplierSelect = (supplier?: Supplier | null) => {
    setState((prevState) => ({
      ...prevState,
      supplierId: supplier?.id || null,
    }));
    search(currentSearchQuery, supplier?.id || null);
  };

  const handleSearchSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    search(currentSearchQuery, supplierId);
  };

  const showSupplierSelect = !!storeId;

  return (
    <>
      <SettingsHeader title="Products" icon={<CubeIcon className="w-6 h-6" />}>
        <Button
          data-testid="product-import-button"
          to={ProductImportPath}
          icon={<DocumentArrowDownIcon className="h-5 w-5 mr-1" />}
          variant="secondary"
        >
          Import
        </Button>
        <Button
          data-testid="product-add-button"
          to={ProductAddPath}
          icon={<PlusIcon className="h-5 w-5 mr-1" />}
          variant="secondary"
        >
          Add Product
        </Button>
      </SettingsHeader>

      <div className="flex flex-col gap-4 mb-8">
        <div className="flex-1">
          <SearchForm
            as="Fragment"
            value={searchQuery}
            onSubmit={handleSearchSubmit}
            onChange={setSearchQuery}
            placeholder="Search by product code or name"
          />
        </div>
        {showSupplierSelect && (
          <div className="flex flex-col lg:flex-row gap-4 lg:items-end">
            <div className="sm:w-96 lg:w-80">
              <SupplierSelectBox value={supplierId} onSelect={handleSupplierSelect} />
            </div>
          </div>
        )}
      </div>

      <ProductTable
        searchQuery={searchQuery}
        page={page}
        onChangePage={handlePageChange}
        supplierId={supplierIdParam}
        productCategoryId={productCategoryId || null}
        createdByStoreId={storeId || null}
      />
    </>
  );
};

export default ProductList;
